import React, { useRef, useEffect, useContext, ReactNode } from 'react';
import classNames from 'classnames';
import Transition from '@/utils/Transition';
import { DropdownContext } from './DropdownContext';

interface DropdownProps {
	button: ReactNode;
	children: ReactNode;
	className?: string;
	top?: string;
	minWidth?: string;
	id: string;
}

const Dropdown: React.FC<DropdownProps> = ({
  button,
  children,
  className,
  top,
  minWidth,
  id,
}) => {
	const { openDropdownId, toggleDropdown } = useContext(DropdownContext);
	const isOpen = openDropdownId === id;

	const trigger = useRef<HTMLDivElement>(null);
	const dropdown = useRef<HTMLDivElement>(null);

	const handleToggle = () => {
		toggleDropdown(id);
	};

	// Close on click outside
	useEffect(() => {
		const clickHandler = (event: MouseEvent) => {
			const target = event.target as Node;

			if (!dropdown.current || !trigger.current) return;
			if (isOpen && !dropdown.current.contains(target) && !trigger.current.contains(target)) {
				toggleDropdown(null);
			}
		};

		document.addEventListener('click', clickHandler);
		return () => {
			document.removeEventListener('click', clickHandler);
		};
	}, [isOpen, toggleDropdown]);

	// Close if the esc key is pressed
	useEffect(() => {
		const keyHandler = (event: KeyboardEvent) => {
			if (isOpen && event.key === 'Escape') {
				toggleDropdown(null);
			}
		};

		document.addEventListener('keydown', keyHandler);
		return () => {
			document.removeEventListener('keydown', keyHandler);
		};
	}, [isOpen, toggleDropdown]);

	const enhancedChildren = React.Children.map(children, (child) => {
		if (React.isValidElement(child)) {
			// Ensure the child can accept onClick
			return React.cloneElement(child, {
				// @ts-ignore
				onClick: (e: MouseEvent<HTMLElement>) => {
					// If the child has its own onClick, call it
					if (child.props.onClick) {
						child.props.onClick(e);
					}
					// Close the Dropdown
					toggleDropdown(null);
				},
			});
		}
		return child;
	});

	return (
		<div className={className ? className : "relative inline-flex max-w-[190px] xxs:max-w-none"}>
			<div
				ref={trigger}
				onClick={handleToggle}
				aria-expanded={isOpen}
				className="w-full"
			>
				{button}
			</div>

			<Transition
				className={classNames(
					"z-[1] absolute bg-[#1A1C1D] rounded-xl bottom-auto left-auto right-0 w-full",
					top ? top : "top-[calc(100%+16px)]",
					minWidth ? minWidth : "min-w-56",
				)}
				show={isOpen}
				enter="transition ease-out duration-200 transform"
				enterStart="opacity-0 -translate-y-2"
				enterEnd="opacity-100 translate-y-0"
				leave="transition ease-out duration-200"
				leaveStart="opacity-100"
				leaveEnd="opacity-0"
			>
				<div
					ref={dropdown}
					className=""
					tabIndex={-1}
					role="menu"
					aria-label="Dropdown menu"
				>
					{enhancedChildren}
				</div>
			</Transition>
		</div>
	);
};

export default Dropdown;
